import $ from 'jquery'

import { DESKTOP_SUBMENU_CLASS } from '../constants/header'

import FlyoutAnimations from './FlyoutAnimations'

const TOGGLE_CLASS = 'main-nav-desktop__nav-mainlinks__item-toggle'

/**
 * Aufklappen per Maus nur dort, wo es wirklich eine Maus gibt.
 *
 * Auf Touchgeraeten loest der erste Tipp den Hover aus und erst der zweite die
 * eigentliche Aktion. Diese Abfrage haelt das Verhalten von Touch fern.
 */
const HAT_MAUS = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover) and (pointer: fine)').matches

/**
 * Aufklappbares Untermenue der Desktop-Navigation.
 *
 * Bedient wird ueber den Umschalter im Menuepunkt, nicht ueber den Menuepunkt selbst.
 * Frueher hing alles am <li>: Es trug tabindex="0", reagierte auf Klick und Enter und
 * meldete aria-expanded. Ein <li> ist aber kein Bedienelement, und neben ihm stand der
 * Link als zweiter Haltepunkt. Name, Rolle und Zustand waren damit auf zwei Elemente
 * verteilt, der Link kuendigte sein Untermenue nicht an.
 *
 * Jetzt: Der Container bleibt der Menuepunkt, weil Klasse "active" und Untermenue dort
 * haengen. Ausloeser ist die Schaltflaeche, und nur sie meldet den Zustand.
 */
class Flyout {
  constructor(container) {
    this.container = container
    this.trigger = $(`.${TOGGLE_CLASS}`, this.container)
    this.flyout = $(`.${DESKTOP_SUBMENU_CLASS}`, this.container)

    this.height = 0
    this.animation = FlyoutAnimations.createAnimation(this.flyout)

    this.isActive = false
    this.activeOutsideWindowListener = null
    this.activeEscapeListener = null

    if (!this.trigger.length || !this.flyout.length) {
      return null
    }

    this.listen()
  }

  listen() {
    // Enter und Leertaste loesen bei einer Schaltflaeche von sich aus einen Klick aus,
    // ein eigener keydown-Handler dafuer entfaellt.
    this.trigger.on('click', this.onTriggerClick.bind(this))
    $(window).on('resize', this.onResize.bind(this))

    /*
     * Aufklappen beim Ueberfahren mit der Maus.
     *
     * Das laeuft bewusst durch dieselben Methoden wie der Klick, damit
     * aria-expanded immer dem sichtbaren Zustand entspricht. Wuerde der Hover am
     * Zustand vorbei oeffnen, meldete die Schaltflaeche "zu", waehrend das
     * Untermenue offen steht.
     *
     * mouseenter und mouseleave steigen nicht auf, sie gelten fuer den
     * Menuepunkt samt Untermenue. Das Untermenue liegt im DOM innerhalb des
     * Menuepunkts, der Weg vom Ausloeser ins Untermenue schliesst also nichts.
     * Das ist die Bedingung "ueberfahrbar" aus WCAG 1.4.13.
     */
    if (HAT_MAUS()) {
      this.container.on('mouseenter', this.onContainerMouseenter.bind(this))
      this.container.on('mouseleave', this.onContainerMouseleave.bind(this))
    }
  }

  onContainerMouseenter() {
    if (this.isActive) {
      return
    }

    this.showFlyout()
  }

  onContainerMouseleave() {
    if (!this.isActive) {
      return
    }

    this.hideFlyout()
  }

  generateEscapeCallback() {
    /*
     * Escape muss auch dann schliessen, wenn das Untermenue nur per Maus offen steht.
     * Der Tastendruck geht dann an das Dokument, nicht an den Menuepunkt; ein Handler
     * am Menuepunkt bekommt ihn nie zu sehen. Das ist die Bedingung "ausblendbar" aus
     * WCAG 1.4.13.
     */
    return (e) => {
      if (e.key !== 'Escape' || !this.isActive) {
        return
      }

      // Den Fokus nur zuruecksetzen, wenn er ohnehin im Menuepunkt liegt. Bei einem
      // per Maus geoeffneten Untermenue waere ein Fokussprung unerwuenscht.
      const fokusInnen = Boolean($(document.activeElement).closest(this.container).length)

      this.hideFlyout()

      if (fokusInnen) {
        this.trigger.trigger('focus')
      }
    }
  }

  listenEscape() {
    this.activeEscapeListener = this.generateEscapeCallback()
    $(document).on('keydown', this.activeEscapeListener)
  }

  unlistenEscape() {
    $(document).off('keydown', this.activeEscapeListener)
    this.activeEscapeListener = null
  }

  onTriggerClick() {
    return !this.isActive ? this.showFlyout() : this.hideFlyout()
  }

  onResize() {
    const height = this.flyout.height()
    // nothing to do if not active or same height as before
    if (!this.isActive || height === this.height) {
      return
    }
    // update height
    this.height = height
    // trigger flyout resize
    this.container.trigger('flyout-resize', [this])
  }

  showFlyout() {
    this.isActive = true
    this.container.addClass('active')
    this.trigger.attr('aria-expanded', 'true')
    // prepare for show
    this.flyout.css('display', 'block')
    this.height = this.flyout.height()
    // animate
    this.animation.play()
    // propagate
    this.container.trigger('flyout-open', [this])
    // close on ouside click
    this.listenOutsideClick()
    this.listenEscape()
  }

  hideFlyout() {
    this.isActive = false
    this.container.removeClass('active')
    this.trigger.attr('aria-expanded', 'false')
    // animate
    this.animation.reverse()
    // propagate
    this.container.trigger('flyout-close', [this])
    // remove outside click listener
    this.unlistenOutsideClick()
    this.unlistenEscape()
  }

  listenOutsideClick() {
    this.activeOutsideWindowListener = this.generateOutsideClickCallback()
    $(window).on('click', this.activeOutsideWindowListener)
  }

  unlistenOutsideClick() {
    $(window).off('click', this.activeOutsideWindowListener)
    this.activeOutsideWindowListener = null
  }

  generateOutsideClickCallback() {
    // Das Ereignis kommt als Argument. Die fruehere Fassung griff auf das globale event
    // zu, was ausserhalb von Chrome nicht verlaesslich ist.
    return (e) => {
      const isOriginInside = Boolean($(e.target).closest(this.container).length)
      if (isOriginInside) {
        return
      }

      this.hideFlyout()
    }
  }
}

export default Flyout
